// src/components/Registros/forms/InsumoForm.jsx
import React, { useState } from 'react';
import { Container, Form, Button, Row, Col, Alert, Table, Badge } from 'react-bootstrap';
import { FaArrowLeft, FaSave, FaTimes, FaPlus, FaTrash } from 'react-icons/fa';
import Swal from 'sweetalert2';
import SelectMult from '../../SettingNot/SelectMult';
import { useNavigate } from 'react-router-dom';
import AdjuntosRegistro from '../AdjuntosRegistro';
import { crearDatos } from '../../../api/common';

const InsumoForm = ({ onClose, onSubmit, tipoRegistro, solicitante}) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    solicitante: solicitante,
    titulo: '',
    fechaInicio: '',
    fechaFin: '',
    urgencia: 'media',
    descripcion: '',
    insumos: [{ nombre: '', cantidad: '', unidad: '', especificaciones: '' }],
    responsablesAprobacion: [],
    adjuntos: []
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const responsables = [
    { id: '1', nombre: 'Juan Pérez', cargo: 'Jefe de Compras' },
    { id: '2', nombre: 'María García', cargo: 'Gerente de Operaciones' }
  ];

  const unidades = ['Unidad', 'Caja', 'Paquete', 'Kilogramo', 'Litro', 'Metro', 'Resma', 'Set'];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleInsumoChange = (index, field, value) => {
    const newInsumos = [...formData.insumos];
    newInsumos[index][field] = value;
    setFormData(prev => ({
      ...prev,
      insumos: newInsumos
    }));
  };

  const agregarInsumo = () => {
    setFormData(prev => ({
      ...prev,
      insumos: [...prev.insumos, { nombre: '', cantidad: '', unidad: '', especificaciones: '' }]
    }));
  };

  const eliminarInsumo = (index) => {
    if (formData.insumos.length === 1) {
      Swal.fire({
        icon: 'warning',
        title: 'Atención',
        text: 'Debe haber al menos un insumo en la solicitud',
        confirmButtonColor: '#0d6efd'
      });
      return;
    }

    setFormData(prev => ({
      ...prev,
      insumos: prev.insumos.filter((_, i) => i !== index)
    }));
  };

  const handleResponsableChange = (e) => {
    const { value } = e.target; // value es un array de IDs: ['id1', 'id2', ...]
    
    // Transformar el array de IDs a objetos con la estructura del modelo
    const responsablesFormateados = value?.map((usuarioId, index) => ({
      usuario: usuarioId,
      orden: index + 1,
      estado: 'pendiente'
    }));
    setFormData(prev => ({
      ...prev,
      responsablesAprobacion: responsablesFormateados
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    // Título se autogenera (ver handleSubmit).

    if (!formData.fechaInicio) {
      newErrors.fechaInicio = 'La fecha de inicio es requerida';
    }

    if (!formData.fechaFin) {
      newErrors.fechaFin = 'La fecha de fin es requerida';
    }

    if (formData.fechaInicio && formData.fechaFin) {
      if (new Date(formData.fechaFin) < new Date(formData.fechaInicio)) {
        newErrors.fechaFin = 'La fecha fin debe ser mayor o igual a la fecha inicio';
      }
    }

    // Validar insumos
    const insumosValidos = formData.insumos.every(insumo => 
      insumo.nombre.trim() && insumo.cantidad && insumo.unidad
    );

    if (!insumosValidos) {
      newErrors.insumos = 'Todos los insumos deben tener nombre, cantidad y unidad';
    }

    if (formData.responsablesAprobacion.length === 0) {
      newErrors.responsablesAprobacion = 'Debe seleccionar al menos un responsable';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      Swal.fire({
        icon: 'error',
        title: 'Errores en el formulario',
        text: 'Por favor, corrija los errores antes de continuar',
        confirmButtonColor: '#0d6efd'
      });
      return;
    }

    setLoading(true);

    try {
      const dataToSubmit = {
        ...formData,
        titulo: 'Solicitud de insumos',
        tipoSolicitud: 'insumo',
        causaInactividad: false
      };

      await crearDatos('Registro', dataToSubmit);

      await Swal.fire({
        icon: 'success',
        title: 'Solicitud Enviada',
        text: 'Tu solicitud de insumos ha sido registrada correctamente',
        confirmButtonColor: '#198754'
      });
      if (onSubmit) onSubmit(dataToSubmit);
      navigate('/Solicitudes');
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Hubo un error al enviar la solicitud. Por favor, intenta nuevamente.',
        confirmButtonColor: '#dc3545'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    Swal.fire({
      title: '¿Estás seguro?',
      text: 'Los datos ingresados se perderán',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Sí, cancelar',
      cancelButtonText: 'No, continuar'
    }).then((result) => {
      if (result.isConfirmed) {
        onClose();
      }
    });
  };

  const getUrgenciaBadge = (urgencia) => {
    const badges = {
      baja: { variant: 'secondary', text: 'Baja' },
      media: { variant: 'primary', text: 'Media' },
      alta: { variant: 'warning', text: 'Alta' },
      critica: { variant: 'danger', text: 'Crítica' }
    };
    return badges[urgencia] || badges.media;
  };

  return (
    <Container fluid className="py-4" style={{ background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)', minHeight: '100vh' }}>
      <div className="form-container">
        <div className="form-header" style={{ background: 'linear-gradient(135deg, #6c757d 0%, #495057 100%)' }}>
          <div className="d-flex align-items-center">
            <Button variant="light" size="sm" onClick={handleCancel} className="me-3">
              <FaArrowLeft />
            </Button>
            <div>
              <h3 className="mb-0 fw-bold">Solicitud de Insumos</h3>
              <p className="mb-0 opacity-75">Complete el formulario para solicitar insumos o materiales</p>
            </div>
          </div>
        </div>

        <div className="form-body">
          <Form onSubmit={handleSubmit}>
            {/* Información Básica */}
            <div className="form-section">
              <h5 className="form-section-title">Información Básica</h5>
              
              <Row className="mb-3">
                <Col md={8}>
                  <Form.Group>
                    <Form.Label className="fw-bold" style={{display:'none'}}>Título (auto)</Form.Label>
                    <Form.Control
                      type="hidden"
                      name="titulo"
                      value={formData.titulo}
                      onChange={handleChange}
                      placeholder="Ej: Insumos de oficina para el área de ventas"
                      isInvalid={!!errors.titulo}
                      maxLength={100}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.titulo}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label className="fw-bold">Nivel de Urgencia *</Form.Label>
                    <Form.Select
                      name="urgencia"
                      value={formData.urgencia}
                      onChange={handleChange}
                    >
                      <option value="baja">Baja</option>
                      <option value="media">Media</option>
                      <option value="alta">Alta</option>
                      <option value="critica">Crítica</option>
                    </Form.Select>
                    <Badge 
                      bg={getUrgenciaBadge(formData.urgencia).variant} 
                      className="mt-2"
                    >
                      {getUrgenciaBadge(formData.urgencia).text}
                    </Badge>
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="fw-bold">Fecha Requerida (Inicio) *</Form.Label>
                    <Form.Control
                      type="date"
                      name="fechaInicio"
                      value={formData.fechaInicio}
                      onChange={handleChange}
                      isInvalid={!!errors.fechaInicio}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.fechaInicio}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label className="fw-bold">Fecha Límite (Fin) *</Form.Label>
                    <Form.Control
                      type="date"
                      name="fechaFin"
                      value={formData.fechaFin}
                      onChange={handleChange}
                      isInvalid={!!errors.fechaFin}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.fechaFin}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>
            </div>

            {/* Lista de Insumos */}
            <div className="form-section">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="form-section-title mb-0">Detalle de Insumos</h5>
                <Button 
                  variant="primary" 
                  size="sm"
                  onClick={agregarInsumo}
                >
                  <FaPlus className="me-2" />
                  Agregar Insumo
                </Button>
              </div>

              {errors.insumos && (
                <Alert variant="danger" className="mb-3">
                  {errors.insumos}
                </Alert>
              )}

              <div className="table-responsive">
                <Table bordered hover>
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: '30%' }}>Nombre del Insumo *</th>
                      <th style={{ width: '15%' }}>Cantidad *</th>
                      <th style={{ width: '15%' }}>Unidad *</th>
                      <th style={{ width: '30%' }}>Especificaciones</th>
                      <th style={{ width: '10%' }}>Acción</th>
                    </tr>
                  </thead>
                  <tbody>
                    {formData.insumos.map((insumo, index) => (
                      <tr key={index}>
                        <td>
                          <Form.Control
                            type="text"
                            value={insumo.nombre}
                            onChange={(e) => handleInsumoChange(index, 'nombre', e.target.value)}
                            placeholder="Nombre del insumo"
                            size="sm"
                          />
                        </td>
                        <td>
                          <Form.Control
                            type="number"
                            value={insumo.cantidad}
                            onChange={(e) => handleInsumoChange(index, 'cantidad', e.target.value)}
                            placeholder="0"
                            min="1"
                            size="sm"
                          />
                        </td>
                        <td>
                          <Form.Select
                            value={insumo.unidad}
                            onChange={(e) => handleInsumoChange(index, 'unidad', e.target.value)}
                            size="sm"
                          >
                            <option value="">Seleccione</option>
                            {unidades.map(unidad => (
                              <option key={unidad} value={unidad}>{unidad}</option>
                            ))}
                          </Form.Select>
                        </td>
                        <td>
                          <Form.Control
                            type="text"
                            value={insumo.especificaciones}
                            onChange={(e) => handleInsumoChange(index, 'especificaciones', e.target.value)}
                            placeholder="Marca, modelo, color, etc."
                            size="sm"
                          />
                        </td>
                        <td className="text-center">
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => eliminarInsumo(index)}
                            disabled={formData.insumos.length === 1}
                          >
                            <FaTrash />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </div>

            {/* Descripción */}
            <div className="form-section">
              <h5 className="form-section-title">Justificación de la Solicitud</h5>
              
              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">Descripción</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  name="descripcion"
                  value={formData.descripcion}
                  onChange={handleChange}
                  placeholder="Describa el motivo de la solicitud, para qué se utilizarán los insumos, etc..."
                  maxLength={500}
                />
                <Form.Text className="text-muted">
                  {formData.descripcion.length}/500 caracteres
                </Form.Text>
              </Form.Group>
            </div>

            {/* Responsables */}
            <div className="form-section">
              <h5 className="form-section-title">Responsables de Aprobación</h5>
              
              <Form.Group>
                <Form.Label className="fw-bold">Seleccione los responsables que deben aprobar *</Form.Label>
                <SelectMult
                //dataDefault={itemData?.userAutorizados}
                handledata={handleResponsableChange}
                name='responsablesAprobacion'
                collection={"Usuarios"}
                field={"nombre"}
                baseFilter={[ // Body para la consuta aggregate de mongo para metrologos y que esten activos.
                        {
                            "$match": {
                            'status': 'activo'
                            } 
                        },
                        {
                            "$lookup": {
                            "from": "roles",
                            "localField": "role",
                            "foreignField": "_id",
                            "as": "result"
                            }
                        },
                        {
                            "$match": {
                            "result.nombre": { $ne: "Cliente" } 
                            }
                        }
                        ]}
                />
                {errors.responsablesAprobacion && (
                  <Alert variant="danger" className="mt-2 py-2">
                    {errors.responsablesAprobacion}
                  </Alert>
                )}
              </Form.Group>
            </div>

            <div className="form-section">
              <h5 className="form-section-title">Adjuntos</h5>
              <AdjuntosRegistro
                tipoSolicitud="insumo"
                adjuntos={formData.adjuntos}
                onChange={(arr) => setFormData(prev => ({ ...prev, adjuntos: arr }))}
                disabled={loading}
              />
            </div>

            {/* Botones */}
            <div className="d-flex justify-content-end gap-2 mt-4">
              <Button 
                variant="secondary" 
                onClick={handleCancel}
                disabled={loading}
                className="px-4"
              >
                <FaTimes className="me-2" />
                Cancelar
              </Button>
              <Button 
                variant="primary" 
                type="submit"
                disabled={loading}
                className="px-4"
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" />
                    Enviando...
                  </>
                ) : (
                  <>
                    <FaSave className="me-2" />
                    Enviar Solicitud
                  </>
                )}
              </Button>
            </div>
          </Form>
        </div>
      </div>
    </Container>
  );
};

export default InsumoForm;